/**
 * Family Portal Service
 * Manages family member registration, permissions, messaging, complaints,
 * activity booking, extra service booking, life story contributions, and surveys.
 */

import LocalStorageService from '../storage/LocalStorageService';

class FamilyPortalService {
  constructor() {
    this.storagePrefix = 'family_portal';
  }

  // ─── Identity & Registration ────────────────────────────────────────

  async inviteFamilyMember(facilityId, residentId, invitation) {
    try {
      const record = {
        id: `INV-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        facilityId,
        residentId,
        email: invitation.email,
        name: invitation.name,
        relationship: invitation.relationship,
        permissionLevel: invitation.permissionLevel || 'view',
        mfaEnabled: invitation.mfaEnabled ?? false,
        status: 'pending',
        invitedAt: new Date().toISOString(),
        invitedBy: invitation.invitedBy,
        token: this._generateToken(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      };

      const invitations = LocalStorageService.get(`${this.storagePrefix}_invitations`, []);
      invitations.push(record);
      LocalStorageService.set(`${this.storagePrefix}_invitations`, invitations);

      console.info('[FamilyPortal] Invitation sent', { id: record.id, email: record.email });
      return { success: true, data: record };
    } catch (error) {
      console.error('[FamilyPortal] inviteFamilyMember failed:', error);
      return { success: false, error: error.message };
    }
  }

  async registerFamilyUser(token, credentials) {
    try {
      const invitations = LocalStorageService.get(`${this.storagePrefix}_invitations`, []);
      const invitation = invitations.find(i => i.token === token && i.status === 'pending');

      if (!invitation) {
        return { success: false, error: 'Invalid or expired invitation token' };
      }
      if (new Date(invitation.expiresAt) < new Date()) {
        return { success: false, error: 'Invitation has expired' };
      }

      const user = {
        id: `FAM-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        email: invitation.email,
        name: invitation.name,
        relationship: invitation.relationship,
        residentId: invitation.residentId,
        facilityId: invitation.facilityId,
        permissionLevel: invitation.permissionLevel,
        mfaEnabled: invitation.mfaEnabled,
        passwordHash: this._hashPassword(credentials.password),
        role: 'family',
        status: 'active',
        registeredAt: new Date().toISOString(),
        lastLogin: null,
        preferences: { emailNotifications: true, pushNotifications: false },
      };

      const users = LocalStorageService.get(`${this.storagePrefix}_users`, []);
      users.push(user);
      LocalStorageService.set(`${this.storagePrefix}_users`, users);

      invitation.status = 'accepted';
      invitation.acceptedAt = new Date().toISOString();
      LocalStorageService.set(`${this.storagePrefix}_invitations`, invitations);

      console.info('[FamilyPortal] User registered', { id: user.id });
      return { success: true, data: { ...user, passwordHash: undefined } };
    } catch (error) {
      console.error('[FamilyPortal] registerFamilyUser failed:', error);
      return { success: false, error: error.message };
    }
  }

  async authenticateFamilyUser(email, password) {
    try {
      const users = LocalStorageService.get(`${this.storagePrefix}_users`, []);
      const user = users.find(u => u.email === email && u.status === 'active');

      if (!user || user.passwordHash !== this._hashPassword(password)) {
        return { success: false, error: 'Invalid email or password' };
      }

      user.lastLogin = new Date().toISOString();
      LocalStorageService.set(`${this.storagePrefix}_users`, users);

      return { success: true, data: { ...user, passwordHash: undefined } };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async requestPasswordReset(email) {
    try {
      const users = LocalStorageService.get(`${this.storagePrefix}_users`, []);
      const user = users.find(u => u.email === email);

      if (!user) {
        return { success: true, message: 'If the email exists, a reset link has been sent.' };
      }

      const resetToken = {
        id: `RST-${Date.now()}`,
        userId: user.id,
        email,
        token: this._generateToken(),
        expiresAt: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
        used: false,
      };

      const tokens = LocalStorageService.get(`${this.storagePrefix}_reset_tokens`, []);
      tokens.push(resetToken);
      LocalStorageService.set(`${this.storagePrefix}_reset_tokens`, tokens);

      console.info('[FamilyPortal] Password reset requested', { email });
      return { success: true, message: 'If the email exists, a reset link has been sent.' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ─── Permission Management ──────────────────────────────────────────

  async updatePermissionLevel(adminUserId, familyUserId, newLevel) {
    const validLevels = ['view', 'comment', 'full'];
    if (!validLevels.includes(newLevel)) {
      return { success: false, error: `Invalid permission level. Must be: ${validLevels.join(', ')}` };
    }

    try {
      const users = LocalStorageService.get(`${this.storagePrefix}_users`, []);
      const user = users.find(u => u.id === familyUserId);
      if (!user) return { success: false, error: 'Family user not found' };

      const previousLevel = user.permissionLevel;
      user.permissionLevel = newLevel;
      user.permissionUpdatedAt = new Date().toISOString();
      user.permissionUpdatedBy = adminUserId;
      LocalStorageService.set(`${this.storagePrefix}_users`, users);

      console.info('[FamilyPortal] Permission updated', { familyUserId, previousLevel, newLevel });
      return { success: true, data: user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  getVisibleFields(permissionLevel) {
    const base = [
      'preferredName', 'photo', 'room', 'simplifiedAlerts',
      'carePlanSummary', 'activitySchedule', 'monthlyCareStatement',
      'selectedProgressNotes', 'photoGallery',
    ];
    const hidden = [
      'clinicalDetail', 'governmentIds', 'financialDetails',
      'staffInformation', 'rawAssessmentScores',
    ];
    return { visible: base, hidden, canComment: permissionLevel !== 'view', canEdit: permissionLevel === 'full' };
  }

  async getResidentPortalView(familyUserId) {
    try {
      const users = LocalStorageService.get(`${this.storagePrefix}_users`, []);
      const familyUser = users.find(u => u.id === familyUserId);
      if (!familyUser) return { success: false, error: 'User not found' };

      const residents = LocalStorageService.get('residents', []);
      const resident = residents.find(r => r.id === familyUser.residentId);
      if (!resident) return { success: false, error: 'Resident not found' };

      const { visible, canComment, canEdit } = this.getVisibleFields(familyUser.permissionLevel);

      const portalView = {
        preferredName: resident.preferredName || resident.name,
        photo: resident.photo || null,
        room: resident.room,
        simplifiedAlerts: (resident.alerts || []).map(a => ({
          type: a.type,
          message: this._simplifyAlertMessage(a.message),
          date: a.date,
        })),
        carePlanSummary: this._generatePlainLanguageSummary(resident),
        activitySchedule: this._getUpcomingActivities(resident.id),
        monthlyCareStatement: this._getLatestCareStatement(resident.id),
        selectedProgressNotes: this._getSelectedProgressNotes(resident.id),
        photoGallery: this._getPhotoGallery(resident.id),
        permissions: { canComment, canEdit, level: familyUser.permissionLevel },
      };

      return { success: true, data: portalView };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ─── Messaging (Care Circle) ────────────────────────────────────────

  async sendMessage(familyUserId, message) {
    try {
      const record = {
        id: `MSG-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        fromUserId: familyUserId,
        fromType: 'family',
        toType: message.toType || 'care_staff',
        toUserId: message.toUserId || null,
        residentId: message.residentId,
        subject: message.subject,
        body: message.body,
        attachments: message.attachments || [],
        status: 'sent',
        readAt: null,
        createdAt: new Date().toISOString(),
      };

      const messages = LocalStorageService.get(`${this.storagePrefix}_messages`, []);
      messages.push(record);
      LocalStorageService.set(`${this.storagePrefix}_messages`, messages);

      console.info('[FamilyPortal] Message sent', { id: record.id });
      return { success: true, data: record };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getMessages(familyUserId, residentId) {
    try {
      const messages = LocalStorageService.get(`${this.storagePrefix}_messages`, []);
      const filtered = messages.filter(
        m => (m.fromUserId === familyUserId || m.toUserId === familyUserId) && m.residentId === residentId
      );
      return { success: true, data: filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async markMessageRead(messageId) {
    try {
      const messages = LocalStorageService.get(`${this.storagePrefix}_messages`, []);
      const msg = messages.find(m => m.id === messageId);
      if (msg) {
        msg.readAt = new Date().toISOString();
        msg.status = 'read';
        LocalStorageService.set(`${this.storagePrefix}_messages`, messages);
      }
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ─── Feedback & Complaints ──────────────────────────────────────────

  async submitFeedback(familyUserId, feedback) {
    try {
      const record = {
        id: `FB-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        familyUserId,
        residentId: feedback.residentId,
        type: feedback.type, // 'complaint' | 'compliment' | 'suggestion'
        category: feedback.category,
        subject: feedback.subject,
        description: feedback.description,
        urgency: feedback.urgency || 'normal',
        status: 'submitted',
        assignedTo: null,
        resolution: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        timeline: [{ action: 'submitted', timestamp: new Date().toISOString(), by: familyUserId }],
      };

      const feedbacks = LocalStorageService.get(`${this.storagePrefix}_feedback`, []);
      feedbacks.push(record);
      LocalStorageService.set(`${this.storagePrefix}_feedback`, feedbacks);

      console.info('[FamilyPortal] Feedback submitted', { id: record.id, type: record.type });
      return { success: true, data: record };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getFeedbackHistory(familyUserId) {
    try {
      const feedbacks = LocalStorageService.get(`${this.storagePrefix}_feedback`, []);
      const filtered = feedbacks.filter(f => f.familyUserId === familyUserId);
      return { success: true, data: filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ─── Activity Booking ───────────────────────────────────────────────

  async getAvailableActivities(facilityId, residentId) {
    try {
      const activities = LocalStorageService.get('activities', []);
      const facilityActivities = activities.filter(a => a.facilityId === facilityId && a.status !== 'cancelled');
      return {
        success: true,
        data: facilityActivities.map(a => ({
          id: a.id,
          name: a.name,
          description: a.description,
          date: a.date,
          time: a.time,
          location: a.location,
          spotsAvailable: (a.maxCapacity || 20) - (a.attendees || []).length,
          isRSVPd: (a.attendees || []).includes(residentId),
        })),
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async rsvpActivity(familyUserId, activityId, residentId, rsvpStatus) {
    try {
      const booking = {
        id: `RSVP-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        activityId,
        residentId,
        bookedBy: familyUserId,
        status: rsvpStatus, // 'attending' | 'declined' | 'tentative'
        createdAt: new Date().toISOString(),
      };

      const bookings = LocalStorageService.get(`${this.storagePrefix}_rsvps`, []);
      const existingIdx = bookings.findIndex(b => b.activityId === activityId && b.residentId === residentId);
      if (existingIdx >= 0) {
        bookings[existingIdx] = { ...bookings[existingIdx], ...booking };
      } else {
        bookings.push(booking);
      }
      LocalStorageService.set(`${this.storagePrefix}_rsvps`, bookings);

      console.info('[FamilyPortal] Activity RSVP', { activityId, status: rsvpStatus });
      return { success: true, data: booking };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ─── Extra Service Booking & Payment ────────────────────────────────

  getServiceCatalogue() {
    return [
      { id: 'svc-hair', name: 'Hairdressing', price: 45.00, category: 'Personal Care', frequency: 'per visit' },
      { id: 'svc-podiatry', name: 'Podiatry', price: 65.00, category: 'Health', frequency: 'per visit' },
      { id: 'svc-massage', name: 'Massage Therapy', price: 80.00, category: 'Wellness', frequency: 'per session' },
      { id: 'svc-newspaper', name: 'Daily Newspaper', price: 30.00, category: 'Lifestyle', frequency: 'per month' },
      { id: 'svc-foxtel', name: 'Foxtel Premium', price: 55.00, category: 'Entertainment', frequency: 'per month' },
      { id: 'svc-phone', name: 'Phone Line Rental', price: 25.00, category: 'Communication', frequency: 'per month' },
      { id: 'svc-laundry', name: 'Premium Laundry', price: 40.00, category: 'Personal Care', frequency: 'per week' },
      { id: 'svc-transport', name: 'Private Transport', price: 120.00, category: 'Transport', frequency: 'per trip' },
      { id: 'svc-physio', name: 'Extra Physiotherapy', price: 90.00, category: 'Health', frequency: 'per session' },
      { id: 'svc-companion', name: 'Companion Outing', price: 150.00, category: 'Social', frequency: 'per outing' },
    ];
  }

  async bookExtraService(familyUserId, booking) {
    try {
      const catalogue = this.getServiceCatalogue();
      const service = catalogue.find(s => s.id === booking.serviceId);
      if (!service) return { success: false, error: 'Service not found in catalogue' };

      const record = {
        id: `SVC-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        familyUserId,
        residentId: booking.residentId,
        serviceId: service.id,
        serviceName: service.name,
        price: service.price,
        requestedDate: booking.requestedDate,
        notes: booking.notes || '',
        paymentMethod: booking.paymentMethod || 'stripe',
        paymentStatus: 'pending',
        stripePaymentIntentId: null,
        status: 'requested',
        createdAt: new Date().toISOString(),
      };

      const bookings = LocalStorageService.get(`${this.storagePrefix}_service_bookings`, []);
      bookings.push(record);
      LocalStorageService.set(`${this.storagePrefix}_service_bookings`, bookings);

      console.info('[FamilyPortal] Extra service booked', { id: record.id, service: service.name });
      return { success: true, data: record };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async processStripePayment(bookingId, stripeToken) {
    try {
      const bookings = LocalStorageService.get(`${this.storagePrefix}_service_bookings`, []);
      const booking = bookings.find(b => b.id === bookingId);
      if (!booking) return { success: false, error: 'Booking not found' };

      booking.paymentStatus = 'completed';
      booking.stripePaymentIntentId = `pi_mock_${Date.now()}`;
      booking.status = 'confirmed';
      booking.paidAt = new Date().toISOString();
      LocalStorageService.set(`${this.storagePrefix}_service_bookings`, bookings);

      console.info('[FamilyPortal] Payment processed (mock Stripe)', { bookingId });
      return { success: true, data: booking };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ─── My Life Story ──────────────────────────────────────────────────

  async addLifeStoryEntry(familyUserId, entry) {
    try {
      const record = {
        id: `LIFE-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        residentId: entry.residentId,
        contributedBy: familyUserId,
        category: entry.category, // 'childhood', 'career', 'family', 'hobbies', 'travel', 'milestones', 'preferences'
        title: entry.title,
        content: entry.content,
        mediaUrls: entry.mediaUrls || [],
        decade: entry.decade || null,
        status: 'pending_review',
        createdAt: new Date().toISOString(),
      };

      const stories = LocalStorageService.get(`${this.storagePrefix}_life_stories`, []);
      stories.push(record);
      LocalStorageService.set(`${this.storagePrefix}_life_stories`, stories);

      console.info('[FamilyPortal] Life story entry added', { id: record.id });
      return { success: true, data: record };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getLifeStory(residentId) {
    try {
      const stories = LocalStorageService.get(`${this.storagePrefix}_life_stories`, []);
      const filtered = stories
        .filter(s => s.residentId === residentId && s.status !== 'rejected')
        .sort((a, b) => (a.decade || '0000').localeCompare(b.decade || '0000'));
      return { success: true, data: filtered };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ─── Satisfaction Surveys ───────────────────────────────────────────

  async getActiveSurveys(familyUserId) {
    try {
      const surveys = LocalStorageService.get(`${this.storagePrefix}_surveys`, []);
      const responses = LocalStorageService.get(`${this.storagePrefix}_survey_responses`, []);
      const completedSurveyIds = responses.filter(r => r.familyUserId === familyUserId).map(r => r.surveyId);

      const active = surveys.filter(s => s.status === 'active' && !completedSurveyIds.includes(s.id));
      return { success: true, data: active };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async submitSurveyResponse(familyUserId, surveyId, answers) {
    try {
      const record = {
        id: `RESP-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        surveyId,
        familyUserId,
        answers,
        submittedAt: new Date().toISOString(),
      };

      const responses = LocalStorageService.get(`${this.storagePrefix}_survey_responses`, []);
      responses.push(record);
      LocalStorageService.set(`${this.storagePrefix}_survey_responses`, responses);

      console.info('[FamilyPortal] Survey response submitted', { surveyId });
      return { success: true, data: record };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  getSurveyTemplates() {
    return [
      {
        id: 'survey-quarterly-q2-2026',
        title: 'Q2 2026 Family Satisfaction Survey',
        description: 'Help us improve the quality of care for your loved one.',
        status: 'active',
        questions: [
          { id: 'q1', text: 'How satisfied are you with the overall care provided?', type: 'rating', scale: 5 },
          { id: 'q2', text: 'How well does staff communicate with you about your loved one?', type: 'rating', scale: 5 },
          { id: 'q3', text: 'How would you rate the cleanliness and maintenance of the facility?', type: 'rating', scale: 5 },
          { id: 'q4', text: 'How satisfied are you with the range of activities offered?', type: 'rating', scale: 5 },
          { id: 'q5', text: 'How would you rate the quality of meals provided?', type: 'rating', scale: 5 },
          { id: 'q6', text: 'How responsive is the staff to your concerns?', type: 'rating', scale: 5 },
          { id: 'q7', text: 'Would you recommend this facility to others?', type: 'nps', scale: 10 },
          { id: 'q8', text: 'What is one thing we could do better?', type: 'text' },
          { id: 'q9', text: 'Any additional comments or suggestions?', type: 'text' },
        ],
        createdAt: '2026-04-01T00:00:00.000Z',
        expiresAt: '2026-06-30T23:59:59.000Z',
      },
    ];
  }

  // ─── Internal Helpers ───────────────────────────────────────────────

  _generateToken() {
    return Array.from({ length: 32 }, () => Math.random().toString(36).charAt(2)).join('');
  }

  _hashPassword(password) {
    let hash = 0;
    for (let i = 0; i < password.length; i++) {
      const char = password.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash |= 0;
    }
    return `pbkdf2_mock_${Math.abs(hash).toString(16)}`;
  }

  _simplifyAlertMessage(message) {
    const jargonMap = {
      'PRN': 'as-needed medication',
      'ADL': 'daily living assistance',
      'UTI': 'urinary infection',
      'BP': 'blood pressure',
      'BGL': 'blood sugar',
      'SpO2': 'oxygen level',
      'NPO': 'not eating/drinking by mouth',
    };
    let simplified = message || '';
    Object.entries(jargonMap).forEach(([term, plain]) => {
      simplified = simplified.replace(new RegExp(`\\b${term}\\b`, 'gi'), plain);
    });
    return simplified;
  }

  _generatePlainLanguageSummary(resident) {
    const parts = [];
    if (resident.careLevel) parts.push(`Care level: ${resident.careLevel}`);
    if (resident.mobility) parts.push(`Mobility: ${resident.mobility}`);
    if (resident.dietaryRequirements) parts.push(`Diet: ${resident.dietaryRequirements}`);
    if (resident.allergies?.length) parts.push(`Allergies: ${resident.allergies.join(', ')}`);
    return parts.length > 0 ? parts.join('. ') + '.' : 'Care plan details being prepared.';
  }

  _getUpcomingActivities(residentId) {
    const bookings = LocalStorageService.get(`${this.storagePrefix}_rsvps`, []);
    return bookings
      .filter(b => b.residentId === residentId && b.status === 'attending')
      .slice(0, 5);
  }

  _getLatestCareStatement(residentId) {
    const statements = LocalStorageService.get(`${this.storagePrefix}_care_statements`, []);
    return statements.filter(s => s.residentId === residentId).sort((a, b) => new Date(b.date) - new Date(a.date))[0] || null;
  }

  _getSelectedProgressNotes(residentId) {
    const notes = LocalStorageService.get('progress_notes', []);
    return notes
      .filter(n => n.residentId === residentId && n.familyVisible)
      .slice(0, 10)
      .map(n => ({ date: n.date, summary: this._simplifyAlertMessage(n.summary), author: n.authorRole }));
  }

  _getPhotoGallery(residentId) {
    const photos = LocalStorageService.get(`${this.storagePrefix}_photos`, []);
    return photos.filter(p => p.residentId === residentId).slice(0, 20);
  }
}

export default new FamilyPortalService();
